import { useState, useCallback, useRef, useEffect } from 'react';
import type {
  AiManager,
  MeetingCheckTemplate,
  MeetingGoal,
  AiAdvice,
  MtgCheckItem,
  NextAction,
  AiEvaluation,
} from '../types/meeting';
import * as api from '../services/api';

// デフォルト設定
const DEFAULT_POLLING_CONFIG = {
  mtgCheck: { interval: 15000, initialDelay: 5000 },
  nextActions: { interval: 20000, initialDelay: 8000 },
  aiEvaluation: { interval: 25000, initialDelay: 10000 },
};

interface UseMeetingProps {
  recordingId: string;
  apiToken: string;
  aiManagers: AiManager[];
  templates: MeetingCheckTemplate[];
  meetingGoals: MeetingGoal[];
}

export const useMeeting = (props: UseMeetingProps) => {
  const { recordingId, apiToken, aiManagers: initialManagers, templates, meetingGoals } = props;

  // 状態
  const [meetingName, setMeetingName] = useState('');
  const [meetingGoal, setMeetingGoal] = useState('');
  const [scheduledDurationMinutes, setScheduledDurationMinutes] = useState(60);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    templates.length > 0 ? templates[0].id : null
  );
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(null);
  const [customGoalText, setCustomGoalText] = useState('');

  const [aiManagers, setAiManagers] = useState<AiManager[]>(
    initialManagers.map((m) => ({ ...m, active: m.is_active !== false }))
  );
  const [aiAdviceHistory, setAiAdviceHistory] = useState<AiAdvice[]>([]);
  const [mtgChecks, setMtgChecks] = useState<MtgCheckItem[]>([]);
  const [nextActions, setNextActions] = useState<NextAction[]>([]);
  const [aiEvaluation, setAiEvaluation] = useState<AiEvaluation>({
    positive: 50,
    understanding: 50,
    activity: 50,
  });

  const [meetingStartTime] = useState<Date>(new Date());
  const [isPollingActive, setIsPollingActive] = useState(false);

  // Refs for polling
  const managerIntervalsRef = useRef<Record<number, ReturnType<typeof setInterval>>>({});
  const mtgCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const nextActionsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const aiEvaluationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const lastSegmentIdsRef = useRef<{
    aiAdvice: Record<number, number>;
    mtgCheck: number;
    nextActions: number;
    aiEvaluation: number;
  }>({
    aiAdvice: {},
    mtgCheck: 0,
    nextActions: 0,
    aiEvaluation: 0,
  });

  const managerAdviceHistoryRef = useRef<Record<number, string[]>>({});

  // テンプレートが選択されたらチェック項目を初期化
  useEffect(() => {
    if (selectedTemplateId) {
      const template = templates.find((t) => t.id === selectedTemplateId);
      if (template?.items) {
        setMtgChecks(
          template.items.map((item) => ({
            id: item.id,
            text: item.item_text,
            completed: false,
            note: undefined,
          }))
        );
      }
    }
  }, [selectedTemplateId, templates]);

  // 経過時間を計算
  const getElapsedSeconds = useCallback(() => {
    return Math.floor((new Date().getTime() - meetingStartTime.getTime()) / 1000);
  }, [meetingStartTime]);

  // 現在の目的・ゴールを取得
  const getCurrentGoal = useCallback(() => {
    if (customGoalText) return customGoalText;
    const goal = meetingGoals.find((g) => g.id === selectedGoalId);
    return goal?.name || '';
  }, [customGoalText, selectedGoalId, meetingGoals]);

  // AIアドバイス取得
  const fetchAiAdvice = useCallback(
    async (manager: AiManager) => {
      if (!recordingId || manager.active === false) return;

      const managerId = manager.id;
      const lastSegmentId = lastSegmentIdsRef.current.aiAdvice[managerId] || 0;
      const previousAdvices = managerAdviceHistoryRef.current[managerId] || [];

      try {
        const response = await api.getAiAdvice({
          recording_id: recordingId,
          manager_id: managerId,
          last_segment_id: lastSegmentId,
          meeting_goal: getCurrentGoal(),
          previous_advices: previousAdvices,
          elapsed_seconds: getElapsedSeconds(),
          scheduled_duration_minutes: scheduledDurationMinutes,
        });

        if (response.success && response.hasNewContent) {
          // 更新
          lastSegmentIdsRef.current.aiAdvice[managerId] = response.lastSegmentId;

          // 履歴に追加
          if (!managerAdviceHistoryRef.current[managerId]) {
            managerAdviceHistoryRef.current[managerId] = [];
          }
          managerAdviceHistoryRef.current[managerId].push(response.advice);

          // UIに追加
          setAiAdviceHistory((prev) => [
            {
              text: response.advice,
              timestamp: new Date(),
              managerId: managerId,
              managerName: response.managerName || manager.name,
            },
            ...prev,
          ]);
        }
      } catch (error) {
        console.error(`Failed to fetch AI advice for manager ${managerId}:`, error);
      }
    },
    [recordingId, getCurrentGoal, getElapsedSeconds, scheduledDurationMinutes]
  );

  // MTGチェック取得
  const fetchMtgCheck = useCallback(async () => {
    if (!recordingId || !selectedTemplateId) return;

    const existingChecks = mtgChecks
      .filter((c) => c.completed)
      .map((c) => ({ index: c.id, note: c.note || '' }));

    try {
      const response = await api.getMtgCheck({
        recording_id: recordingId,
        template_id: selectedTemplateId,
        last_segment_id: lastSegmentIdsRef.current.mtgCheck,
        meeting_goal: getCurrentGoal(),
        existing_checks: existingChecks,
      });

      if (response.success && response.hasNewContent) {
        lastSegmentIdsRef.current.mtgCheck = response.lastSegmentId;

        if (response.mtgCheck?.completed) {
          setMtgChecks((prev) =>
            prev.map((item, idx) => {
              const itemIndex = idx + 1;
              if (response.mtgCheck.completed.includes(itemIndex)) {
                const note =
                  response.mtgCheck.notes?.[itemIndex] ||
                  response.mtgCheck.notes?.[String(itemIndex)] ||
                  '';
                return { ...item, completed: true, note };
              }
              return item;
            })
          );
        }
      }
    } catch (error) {
      console.error('Failed to fetch MTG check:', error);
    }
  }, [recordingId, selectedTemplateId, mtgChecks, getCurrentGoal]);

  // 次回までにやるべきこと取得
  const fetchNextActions = useCallback(async () => {
    if (!recordingId) return;

    const existingActions = nextActions.map((a) => a.task);

    try {
      const response = await api.getNextActions({
        recording_id: recordingId,
        last_segment_id: lastSegmentIdsRef.current.nextActions,
        meeting_goal: getCurrentGoal(),
        existing_actions: existingActions,
      });

      if (response.success && response.hasNewContent) {
        lastSegmentIdsRef.current.nextActions = response.lastSegmentId;

        if (response.nextActions) {
          setNextActions(response.nextActions);
        }
      }
    } catch (error) {
      console.error('Failed to fetch next actions:', error);
    }
  }, [recordingId, nextActions, getCurrentGoal]);

  // AI評価取得
  const fetchAiEvaluation = useCallback(async () => {
    if (!recordingId) return;

    try {
      const response = await api.getAiEvaluation({
        recording_id: recordingId,
        last_segment_id: lastSegmentIdsRef.current.aiEvaluation,
        previous_evaluation: aiEvaluation,
      });

      if (response.success && response.hasNewContent) {
        lastSegmentIdsRef.current.aiEvaluation = response.lastSegmentId;

        if (response.aiEvaluation) {
          setAiEvaluation(response.aiEvaluation);
        }
      }
    } catch (error) {
      console.error('Failed to fetch AI evaluation:', error);
    }
  }, [recordingId, aiEvaluation]);

  // ポーリング開始
  const startPolling = useCallback(() => {
    if (isPollingActive) return;
    setIsPollingActive(true);

    // AIマネージャーごとのポーリング
    aiManagers
      .filter((m) => m.active !== false)
      .forEach((manager) => {
        const interval = (manager.execution_interval_seconds || 60) * 1000;
        managerIntervalsRef.current[manager.id] = setInterval(() => {
          fetchAiAdvice(manager);
        }, interval);
      });

    // MTGチェックポーリング
    mtgCheckIntervalRef.current = setInterval(
      fetchMtgCheck,
      DEFAULT_POLLING_CONFIG.mtgCheck.interval
    );
    setTimeout(fetchMtgCheck, DEFAULT_POLLING_CONFIG.mtgCheck.initialDelay);

    // 次回までにやるべきことポーリング
    nextActionsIntervalRef.current = setInterval(
      fetchNextActions,
      DEFAULT_POLLING_CONFIG.nextActions.interval
    );
    setTimeout(fetchNextActions, DEFAULT_POLLING_CONFIG.nextActions.initialDelay);

    // AI評価ポーリング
    aiEvaluationIntervalRef.current = setInterval(
      fetchAiEvaluation,
      DEFAULT_POLLING_CONFIG.aiEvaluation.interval
    );
    setTimeout(fetchAiEvaluation, DEFAULT_POLLING_CONFIG.aiEvaluation.initialDelay);
  }, [
    isPollingActive,
    aiManagers,
    fetchAiAdvice,
    fetchMtgCheck,
    fetchNextActions,
    fetchAiEvaluation,
  ]);

  // ポーリング停止
  const stopPolling = useCallback(() => {
    setIsPollingActive(false);

    // AIマネージャーのインターバルをクリア
    Object.values(managerIntervalsRef.current).forEach(clearInterval);
    managerIntervalsRef.current = {};

    // その他のインターバルをクリア
    if (mtgCheckIntervalRef.current) clearInterval(mtgCheckIntervalRef.current);
    if (nextActionsIntervalRef.current) clearInterval(nextActionsIntervalRef.current);
    if (aiEvaluationIntervalRef.current) clearInterval(aiEvaluationIntervalRef.current);
  }, []);

  // AIマネージャーの有効/無効切り替え
  const toggleAiManager = useCallback(
    (managerId: number) => {
      setAiManagers((prev) =>
        prev.map((m) => {
          if (m.id === managerId) {
            const newActive = !m.active;

            // ポーリング中の場合、インターバルを開始/停止
            if (isPollingActive) {
              if (newActive) {
                const interval = (m.execution_interval_seconds || 60) * 1000;
                managerIntervalsRef.current[managerId] = setInterval(() => {
                  fetchAiAdvice({ ...m, active: true });
                }, interval);
              } else {
                if (managerIntervalsRef.current[managerId]) {
                  clearInterval(managerIntervalsRef.current[managerId]);
                  delete managerIntervalsRef.current[managerId];
                }
              }
            }

            return { ...m, active: newActive };
          }
          return m;
        })
      );
    },
    [isPollingActive, fetchAiAdvice]
  );

  // 議事録生成（DB保存のみ、ダウンロードなし）- C#から呼び出し用
  const generateMinutesOnly = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!recordingId) {
      return { success: false, error: '会議が開始されていません。' };
    }

    try {
      const template = templates.find((t) => t.id === selectedTemplateId);
      const response = await api.generateMinutes({
        recording_id: recordingId,
        token: apiToken,
        meeting_name: meetingName,
        meeting_goal: getCurrentGoal(),
        template_name: template?.template_name || 'MTGチェック',
        mtg_checks: mtgChecks.map((check) => ({
          text: check.text,
          completed: check.completed,
          note: check.note,
        })),
        todos: nextActions.map((a) => a.task),
        ai_evaluation: aiEvaluation,
        ai_advices: aiAdviceHistory.map((advice) => ({
          manager_name: advice.managerName || '',
          text: advice.text,
        })),
      });

      if (response.success) {
        return { success: true };
      } else {
        return { success: false, error: response.error || '議事録の生成に失敗しました。' };
      }
    } catch (error) {
      console.error('Failed to generate minutes:', error);
      const errorMessage = error instanceof Error ? error.message : '議事録の生成に失敗しました。';
      return { success: false, error: errorMessage };
    }
  }, [
    recordingId,
    apiToken,
    meetingName,
    getCurrentGoal,
    selectedTemplateId,
    templates,
    mtgChecks,
    nextActions,
    aiEvaluation,
    aiAdviceHistory,
  ]);

  // 議事録生成（ダウンロード付き）- UI用
  const generateAndDownloadMinutes = useCallback(async () => {
    if (!recordingId) {
      alert('会議が開始されていません。');
      return;
    }

    try {
      const template = templates.find((t) => t.id === selectedTemplateId);
      const response = await api.generateMinutes({
        recording_id: recordingId,
        token: apiToken,
        meeting_name: meetingName,
        meeting_goal: getCurrentGoal(),
        template_name: template?.template_name || 'MTGチェック',
        mtg_checks: mtgChecks.map((check) => ({
          text: check.text,
          completed: check.completed,
          note: check.note,
        })),
        todos: nextActions.map((a) => a.task),
        ai_evaluation: aiEvaluation,
        ai_advices: aiAdviceHistory.map((advice) => ({
          manager_name: advice.managerName || '',
          text: advice.text,
        })),
      });

      if (response.success) {
        // ダウンロード
        const blob = new Blob([response.markdown], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to generate minutes:', error);
      alert('議事録の生成に失敗しました。');
    }
  }, [
    recordingId,
    apiToken,
    meetingName,
    getCurrentGoal,
    selectedTemplateId,
    templates,
    mtgChecks,
    nextActions,
    aiEvaluation,
    aiAdviceHistory,
  ]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    // 状態
    meetingName,
    setMeetingName,
    meetingGoal,
    setMeetingGoal,
    scheduledDurationMinutes,
    setScheduledDurationMinutes,
    selectedTemplateId,
    setSelectedTemplateId,
    selectedGoalId,
    setSelectedGoalId,
    customGoalText,
    setCustomGoalText,
    aiManagers,
    aiAdviceHistory,
    mtgChecks,
    nextActions,
    aiEvaluation,
    templates,
    meetingGoals,
    isPollingActive,

    // アクション
    toggleAiManager,
    startPolling,
    stopPolling,
    generateAndDownloadMinutes,
    generateMinutesOnly,
    getCurrentGoal,
  };
};
